import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import '../../services/GlobalVariables';

const initialState = {
    holidays: [],
    upcomingHolidays: [],
    loading: false,
    error: null,
    success: false
};

// Get all gazetted holidays
export const getGazettedHolidays = createAsyncThunk(
    'gazettedHolidays/getAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${global.SET_API_URL}gazettedHolidays`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch holidays');
        }
    }
);

// Get upcoming holidays
export const getUpcomingHolidays = createAsyncThunk(
    'gazettedHolidays/getUpcoming',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${global.SET_API_URL}gazettedHolidays/upcoming`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch upcoming holidays');
        }
    }
);

// Get holidays by date range
export const getHolidaysByDateRange = createAsyncThunk(
    'gazettedHolidays/getByRange',
    async ({ startDate, endDate }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${global.SET_API_URL}gazettedHolidays/range?startDate=${startDate}&endDate=${endDate}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch holidays');
        }
    }
);

// Add new holiday
export const addGazettedHoliday = createAsyncThunk(
    'gazettedHolidays/add',
    async (holidayData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${global.SET_API_URL}gazettedHolidays`, holidayData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to add holiday');
        }
    }
);

// Update holiday
export const updateGazettedHoliday = createAsyncThunk(
    'gazettedHolidays/update',
    async ({ holidayID, holidayData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${global.SET_API_URL}gazettedHolidays/${holidayID}`,
                holidayData
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to update holiday');
        }
    }
);

// Delete holiday
export const deleteGazettedHoliday = createAsyncThunk(
    'gazettedHolidays/delete',
    async (holidayID, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${global.SET_API_URL}gazettedHolidays/${holidayID}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || 'Failed to delete holiday');
        }
    }
);

const gazettedHolidaysSlice = createSlice({
    name: 'gazettedHolidays',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get all holidays
            .addCase(getGazettedHolidays.pending, (state) => {
                state.loading = true;
            })
            .addCase(getGazettedHolidays.fulfilled, (state, action) => {
                state.loading = false;
                state.holidays = action.payload;
            })
            .addCase(getGazettedHolidays.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get upcoming holidays
            .addCase(getUpcomingHolidays.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUpcomingHolidays.fulfilled, (state, action) => {
                state.loading = false;
                state.upcomingHolidays = action.payload;
            })
            .addCase(getUpcomingHolidays.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Get by date range
            .addCase(getHolidaysByDateRange.pending, (state) => {
                state.loading = true;
            })
            .addCase(getHolidaysByDateRange.fulfilled, (state, action) => {
                state.loading = false;
                state.holidays = action.payload;
            })
            .addCase(getHolidaysByDateRange.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add holiday
            .addCase(addGazettedHoliday.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(addGazettedHoliday.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(addGazettedHoliday.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update holiday
            .addCase(updateGazettedHoliday.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(updateGazettedHoliday.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(updateGazettedHoliday.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Delete holiday
            .addCase(deleteGazettedHoliday.pending, (state) => {
                state.loading = true;
                state.success = false;
            })
            .addCase(deleteGazettedHoliday.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
            })
            .addCase(deleteGazettedHoliday.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearError, clearSuccess } = gazettedHolidaysSlice.actions;
export default gazettedHolidaysSlice.reducer;
